import React, { useContext, useEffect, useRef } from 'react';
import { Link } from '@reach/router';
import ls from 'local-storage';
import cx from 'classnames';
import { getAppTokenName, getUrl, APPS } from '@squirrellearningltd/go-shared-utils';
import { AuthContext } from '../../services/auth/AuthProvider';
import { APP_NAME } from '../../services/constants';

import styles from './Dashboard.module.scss';
import { useTranslation } from 'react-i18next';
import write from '../../images/write_circle.png'
import loader from '../../images/loader-boomer.png'
import read from '../../images/read_circle.png'
import rewards from '../../images/rewards_circle.png'
import gem from './images/gem.svg';
import boombassador from '../../images/boombassador.png'
import boombassadorLogo from '../../images/new_dashboard/boombassadorLogo.png';


// New Dashboard

//Images
import boomerAvatarBackground from '../../images/new_dashboard/boomer-avatar-background.png';
import linkBoomerRead from '../../images/new_dashboard/link-boomer-read.png';
import linkBoomerWrite from '../../images/new_dashboard/link-boomer-write.png';
import linkBoomerRewards from '../../images/new_dashboard/link--boomer-rewards.png';
import linkBoomerBoutique from '../../images/new_dashboard/link-boomer-boutique.png';
import logsBoomer from '../../images/boomer.svg';
import logsHome from '../../images/new_dashboard/home-reading-logs.svg';
import newsBoomer from '../../images/new_dashboard/news-boomer.svg';
import progressboomer from '../../images/new_dashboard/progress-boomer.svg';
import progressBooksRead from '../../images/new_dashboard/progress-book-read.svg';
import progressGemsEarned from '../../images/new_dashboard/progress-gems-earned.svg';
import progressReadingStreak from '../../images/new_dashboard/progress-reading-streak.svg';
import announcmentsBoomer from '../../images/new_dashboard/announcments-boomer.svg';


// Components
import { ProgressBar, ProgressItems } from '@squirrellearningltd/go-pupil-components';
import NewsCard from './components/news-card/NewsCardComponent';
import AnnouncmentCard from './components/announcments/AnnouncmentCard';
import NavigationCard from './components/navigation-card/NavigationCard';

import { ArrowRight, Target, MoreVertical } from 'react-feather';
import LogoutButton from '../../components/LogoutButton';
import { useQuery } from '@apollo/react-hooks';
import { NOTIFICATIONS_BY_STATUS, GET_STUDENT_READING_LOGS_TARGET } from './graphql';
import moment from 'moment';

const DAILY_GEM_TARGET = 3;

const Dashboard = () => {

    // Weekly reading targets
    const getStartOfWeek = (startDay) => {
        const date = new Date();
        // Convert backend day (1–7, where 1 = Monday) to JS day (0–6, where 0 = Sunday)
        const jsStartDay = startDay % 7;

        const currentDay = date.getDay(); // JS weekday (0 = Sunday, ..., 6 = Saturday)

        // Calculate distance to go back to the desired start of the week
        const diff = (currentDay - jsStartDay + 7) % 7;

        // Subtract the difference to get the starting date
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - diff);

        return startOfWeek;
    }

    const { t } = useTranslation();
    const token = ls(getAppTokenName(APP_NAME));
    const accountMenuRef = useRef(null);

    useEffect(() => {
        const closeAccountMenu = (event) => {
            const menu = accountMenuRef.current;
            if (!menu || !menu.open) return;

            if (event.type === 'keydown') {
                if (event.key !== 'Escape') return;
                menu.open = false;
                menu.querySelector('summary').focus();
            } else if (!menu.contains(event.target)) {
                menu.open = false;
            }
        };

        document.addEventListener('click', closeAccountMenu);
        document.addEventListener('keydown', closeAccountMenu);
        return () => {
            document.removeEventListener('click', closeAccountMenu);
            document.removeEventListener('keydown', closeAccountMenu);
        };
    }, []);



    const { currentUser } = useContext(AuthContext);

    const isBoombassador = currentUser.isBoombassador;

    const { data: notificationsData } = useQuery(NOTIFICATIONS_BY_STATUS, {
        variables: {
            status: 1
        },
    });

    const { data: readingLogsTargetData } = useQuery(GET_STUDENT_READING_LOGS_TARGET, {
        variables: {
            weekStart: moment(getStartOfWeek(currentUser.school.weekStart)).format('DD/MM/YYYY'),
        },
    });

    const getProgressPercentage = (progress = 0, target = 0) => {
        if (!target) return 0;

        return Math.min(100, Math.max(0, Math.round((progress / target) * 100)));
    }

    const dailyGemProgress = getProgressPercentage(currentUser.dailyTotalGemsEarned, DAILY_GEM_TARGET);
    const notifications = notificationsData?.notificationsByStatus || [];
    const weeklyReport = readingLogsTargetData?.currentStudent?.weeklyStudentReportData?.[0];
    const studentLogsProgress = getProgressPercentage(
        weeklyReport?.studentLogsCount,
        weeklyReport?.studentLogsTarget
    );
    const parentLogsProgress = getProgressPercentage(
        weeklyReport?.parentLogsCount,
        weeklyReport?.parentLogsTarget
    );

    return (
        <>
            <div className={styles.dashboardContainer}>
                <div className={styles.leftSide}>
                    <div className={styles.boomerInfoWrapper}>
                        <div
                            className={cx(styles.boomerAvatar, { [styles.boombassadorAvatar]: isBoombassador })}
                            style={{
                                backgroundImage: `url(${JSON.stringify(currentUser.backgroundImageUrl || boomerAvatarBackground)})`,

                            }}>
                            <img
                                src={currentUser.boomerAvatarUrl ? `${currentUser.boomerAvatarUrl}?v=${currentUser.updatedAt.replace(/\s/g, '')}` : loader}
                                className={styles.welcomeBoomer}
                            />
                        </div>
                        <div className={styles.boomerInfo}>
                            <h1 className={styles.boomerName}>{currentUser.firstName} {currentUser.lastName}</h1>
                            <div className={styles.boomerGems}>
                                <img src={gem} className={styles.gem} alt={t('gem')} />
                                <p>{currentUser.availableGems} <span className={styles.desktopGemsLabel}>{currentUser.availableGems === 1 ? t('gem') : t('gems')}</span></p>
                            </div>
                            <div className={styles.boomerCollectedgems}>
                                <p className={styles.progressBarTitle}>{currentUser.dailyTotalGemsEarned}/{DAILY_GEM_TARGET} <span className={styles.desktopChestLabel}>Gem chests collected today</span><span className={styles.mobileChestLabel}>chests today</span></p>
                                <ProgressBar
                                    showProgressbadge
                                    progress={dailyGemProgress}
                                    progressBadge={gem} />
                            </div>
                        </div>
                        <details ref={accountMenuRef} className={styles.mobileAccountMenu}>
                            <summary className={styles.mobileAccountMenuToggle} aria-label="Account menu">
                                <MoreVertical aria-hidden="true" />
                            </summary>
                            <div className={styles.mobileAccountMenuDropdown}>
                                <LogoutButton classes={styles.mobileLogoutButton}>Logout</LogoutButton>
                            </div>
                        </details>
                        {isBoombassador && (
                            <a
                                className={styles.mobileBoombassadorLink}
                                href={`${getUrl(APPS.go_read_pupil)}/boombassador?token=${token}`}
                                aria-label="Open Boombassador"
                            >
                                <img src={boombassadorLogo} alt="Boombassador" />
                                <span>Open <ArrowRight aria-hidden="true" /></span>
                            </a>
                        )}
                    </div>

                    <div className={styles.boomerNavigationLinks}>
                        {/* Until writefest is finished, all enrolled students should have access to the write functionality */}
                        {/* <a className={cx(styles.appLink, { [styles.appLinkDisabled]: !currentUser.school.canAccessBoomWriteApp }) } href={`${getUrl(APPS.go_write_pupil)}?token=${token}`}> */}
                        <a
                            className={cx(styles.boomerLink, styles.readLink, { [styles.boomerLinkDisabled]: currentUser.books && !currentUser.books.length })}
                            href={`${getUrl(APPS.go_read_pupil)}?token=${token}`}
                        >
                            <NavigationCard
                                featured
                                headline='Read'
                                subheadline='Log your reading'
                                image={linkBoomerRead}
                            />
                        </a>
                        <a
                            className={cx(styles.boomerLink, styles.writeLink, { [styles.boomerLinkkDisabled]: !currentUser.school.canAccessGoReadApp })}
                            href={`${getUrl(APPS.go_write_pupil)}?token=${token}`}
                        >
                            <NavigationCard
                                headline='Write'
                                subheadline='Create a story'
                                image={linkBoomerWrite}
                            />
                        </a>

                        <Link to='/rewards' className={cx(styles.boomerLink, styles.rewardsLink)}>
                            <NavigationCard
                                headline='Rewards'
                                subheadline='Spend your gems'
                                image={linkBoomerRewards}
                            />

                        </Link>
                        <Link to='/boomer' className={cx(styles.boomerLink, styles.boomerBoutiqueLink)}>
                            <NavigationCard
                                headline='Boomer'
                                subheadline='Customise your boomer'
                                image={linkBoomerBoutique}
                            />
                        </Link>
                    </div>

                </div>
                <div className={styles.rightSide}>
                    <div className={styles.readingTargetsWrapper}>
                        <div className={styles.readingTargetheaderWrapper}>
                            <Target size={40} color='#CF3636' />
                            <h2 className={styles.readingTargetsHeader}>Your Targets</h2>
                        </div>

                        <div className={styles.readingTargetLogs}>
                            <img
                                src={logsBoomer}
                                width={40}
                                alt="Boomer Logs"
                                className={styles.readingTargetLogsImg}
                                title={`You have ${weeklyReport?.studentLogsCount ?? 0} / ${weeklyReport?.studentLogsTarget ?? 0} this week`}
                            />

                            <div className={styles.readingTargetStats}> {weeklyReport?.studentLogsCount ?? 0} / {weeklyReport?.studentLogsTarget ?? 0}</div>
                            <div
                                className={styles.readingTargetProgress}
                            >
                                <div
                                    className={styles.progressTooltipWrapper}
                                    title={`You have ${weeklyReport?.studentLogsCount ?? 0} / ${weeklyReport?.studentLogsTarget ?? 0} this week`}>
                                    <ProgressBar
                                        progress={studentLogsProgress}
                                        backgroundColor='#1B8DFD'
                                    />
                                </div>
                            </div>

                        </div>
                        <div className={styles.readingTargetLogs}>
                            <img
                                src={logsHome} width={40}
                                alt="Home Logs"
                                className={styles.readingTargetLogsImg}
                                title={`You have ${weeklyReport?.parentLogsCount ?? 0} / ${weeklyReport?.parentLogsTarge ?? 0} this week`} />

                            <div className={styles.readingTargetStats}> {weeklyReport?.parentLogsCount ?? 0} / {weeklyReport?.parentLogsTarge ?? 0}</div>
                            <div
                                className={styles.readingTargetProgress}
                            >
                                <div
                                    className={styles.progressTooltipWrapper}
                                    title={`You have ${weeklyReport?.parentLogsCount ?? 0} / ${weeklyReport?.parentLogsTarge ?? 0} this week`}
                                >
                                    <ProgressBar
                                        progress={parentLogsProgress}
                                        backgroundColor='#1B8DFD'
                                    />
                                </div>

                            </div>

                        </div>
                    </div>

                    <div className={styles.newProgressAnnouncmentsWrapper}>
                        <div className={styles.newsContainer}>
                            <div className={styles.newsHeadlineContainer}>
                                <img src={newsBoomer} width={40} alt="Home Logs" />
                                <h2 className={styles.newsHeadline}>Boom News</h2>
                            </div>

                            <div className={styles.newsCardsContainer}>
                                {notifications && notifications.length? notifications.map(notification => (
                                    <NewsCard
                                        key={notification.id}
                                        artworkUrl={notification.fileUrl}
                                        message={notification.message}
                                    />
                                )) : 'No news right now!'}
                            </div>

                        </div>

                         {/* Temporrary disabled        */}
                        {/* <div className={styles.progressAnnouncmentsWrapper}>
                            <div className={styles.boomerProgress}>
                                <div className={styles.progressHeadlineWrapper}>
                                    <img src={progressboomer} alt='Boomer progress' />
                                    <h3 className={styles.progressHeadline}>Your Progress</h3>
                                </div>

                                <ProgressItems
                                    items={[
                                        { imageSrc: progressBooksRead, value: currentUser.academicYearTotalBooksCompleted, description: 'Books Read' },
                                        { imageSrc: progressGemsEarned, value: currentUser.academicYearTotalGemsEarned, description: 'Gems Earned' },
                                        { imageSrc: progressReadingStreak, value: currentUser.dailyStreak, description: 'Day Streak' },
                                    ]}
                                />
                            </div>

                            <div className={styles.boomerAnnouncments}>
                                <div className={styles.announcmentsHeadlineWrapper}>
                                    <img src={announcmentsBoomer} alt='Boomer announcments' />
                                    <h3 className={styles.announcmentsHeadline}>Announcements </h3>
                                </div>

                                <div>
                                    <AnnouncmentCard
                                        announcmentText='Keep reading I want to see you reach your reading targets this week!'
                                        dateText='31st October'
                                    />
                                    <AnnouncmentCard
                                        announcmentText='You have read really well this week Harriete keep it up!'
                                        dateText='27th September'
                                    />
                                    <AnnouncmentCard
                                        announcmentText='You have read really well this week Harriete keep it up!'
                                        dateText='27th September'
                                    />
                                    <AnnouncmentCard
                                        announcmentText='You have read really well this week Harriete keep it up!'
                                        dateText='22nd August'
                                    />
                                </div>
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>


            {/* <div className={styles.dashboard}> */}
            {/* <div className={styles.welcome}>
                    <div className={`${styles.welcomeInner} ${styles.welcomeInnerMargin}`}>
                        <div className={styles.welcomeBoomerHolder}>
                            <img src={currentUser.boomerAvatarUrl ? currentUser.boomerAvatarUrl : loader} className={styles.welcomeBoomer} />
                        </div>
                        <h1 className='u-bold u-bottom-tight'>{currentUser.firstName} {currentUser.lastName}</h1>
                        <div className={styles.gemPanel}>
                            <img src={gem} className={styles.gem} alt={t('gem')} />
                            <p>{currentUser.availableGems} {currentUser.availableGems === 1 ? t('gem') : t('gems')}</p>
                        </div>
                    </div>
                    {isBoombassador && (
                        <div className={styles.welcomeBoombassador}>
                            <div >
                                <a href={`${getUrl(APPS.go_read_pupil)}/boombassador/?token=${token}`}>
                                    <img src={boombassador} className={styles.boombassadorImg} />
                                </a>
                            </div>
                        </div>
                    )}
                </div> */}
            {/* <div className={styles.appLinks}> */}
            {/* Until writefest is finished, all enrolled students should have access to the write functionality */}
            {/* <a className={cx(styles.appLink, { [styles.appLinkDisabled]: !currentUser.school.canAccessBoomWriteApp }) } href={`${getUrl(APPS.go_write_pupil)}?token=${token}`}> */}
            {/* <a className={cx(styles.appLink, { [styles.appLinkDisabled]: currentUser.books && !currentUser.books.length })} href={`${getUrl(APPS.go_write_pupil)}?token=${token}`}>

                        <img src={write} className={styles.iconImg} />
                        <span>Write</span>
                    </a>
                    <a className={cx(styles.appLink, { [styles.appLinkDisabled]: !currentUser.school.canAccessGoReadApp })} href={`${getUrl(APPS.go_read_pupil)}?token=${token}`}>
                        <img src={read} className={styles.iconImg} />
                        <span>Read</span>
                    </a> */}

            {/* <Link to='/rewards' className={styles.appLink}>
                        <img src={rewards} className={styles.iconImg} />
                        <span>Rewards</span>
                    </Link>
                    <Link to='/boomer' className={styles.appLink}>
                        <div className={styles.iconHolder}>
                            <img src={currentUser.boomerAvatarUrl ? `${currentUser.boomerAvatarUrl}?v=${currentUser.updatedAt.replace(/\s/g, '')}` : loader} className={styles.iconImg} />
                        </div>
                        <span>Boomer</span>
                    </Link> */}
            {/* </div> */}
            {/* </div> */}
        </>
    )
}

export default Dashboard;
